import { Interest } from "@/schemas/profile/attributes/UserInterestAttributeSet";
import { Language } from "@/schemas/profile/attributes/UserLanguageAttributeSet";
import { Seeking } from "@/schemas/profile/attributes/UserSeekingAttributeSet";
import { Height } from "@/schemas/profile/attributes/UserHeightAttributeSet";
import {
  CompatibilityType,
  RecommendationCategory,
  RecommendationHighlightType,
  UserRecommendedItem,
} from "@/schemas/user/explore";
import { UserImageType } from "@/schemas/user/image";
import { Ethnicity } from "@/schemas/profile/attributes/UserEthnicityAttributeSet";
import { BodyType } from "@/schemas/profile/attributes/UserBodyAttributeSet";
import { Weight } from "@/schemas/profile/attributes/UserWeightAttributeSet";
import { RolePosition } from "@/schemas/profile/attributes/UserRoleAttributeSet";

/**
 * Category priority metadata for profiles
 * Maps profile IDs to the categories where they should be featured and their priority rank
 */
export type ProfileCategoryPriority = {
  profileId: string;
  categories: {
    category: RecommendationCategory;
    priority: number; // Lower number = higher priority (1 = first)
  }[];
};

/**
 * Category priorities for Caucasian themed profiles
 * Defines which profiles should appear first in specific recommendation categories
 *
 * Available categories: TRENDING, DATING, FRIENDS, ONLINE_NOW, NEW_USERS, NEARBY
 */
export const CAUCASIAN_PROFILE_CATEGORY_PRIORITIES: ProfileCategoryPriority[] =
  [
    {
      profileId: "MKT001", // westbourne - London
      categories: [
        { category: RecommendationCategory.TRENDING, priority: 1 },
        { category: RecommendationCategory.FRIENDS, priority: 2 },
        { category: RecommendationCategory.NEW_USERS, priority: 1 },
      ],
    },
    {
      profileId: "MKT002", // nachtleben - Berlin
      categories: [
        { category: RecommendationCategory.TRENDING, priority: 2 },
        { category: RecommendationCategory.ONLINE_NOW, priority: 1 },
      ],
    },
    {
      profileId: "MKT003", // grachten - Amsterdam
      categories: [
        { category: RecommendationCategory.DATING, priority: 1 },
        { category: RecommendationCategory.ONLINE_NOW, priority: 2 },
      ],
    },
    {
      profileId: "MKT004", // lumiere - Paris
      categories: [
        { category: RecommendationCategory.FRIENDS, priority: 3 },
        { category: RecommendationCategory.NEW_USERS, priority: 2 },
      ],
    },
    {
      profileId: "MKT005", // solsticio - Barcelona
      categories: [
        { category: RecommendationCategory.DATING, priority: 2 },
        { category: RecommendationCategory.ONLINE_NOW, priority: 3 },
      ],
    },
    {
      profileId: "MKT007", // claddagh - Dublin
      categories: [
        { category: RecommendationCategory.FRIENDS, priority: 1 },
        { category: RecommendationCategory.NEW_USERS, priority: 3 },
      ],
    },
    {
      profileId: "MKT008", // maretide - Lisbon
      categories: [
        { category: RecommendationCategory.NEARBY, priority: 1 },
        { category: RecommendationCategory.DATING, priority: 3 },
      ],
    },
    {
      profileId: "MKT009", // havnero - Copenhagen
      categories: [
        { category: RecommendationCategory.TRENDING, priority: 3 },
        { category: RecommendationCategory.FRIENDS, priority: 4 },
      ],
    },
  ];

/**
 * Caucasian themed mock data - used for demos and presentations
 * Contains 9 polished profiles with professional images and complete attributes
 * Showcases the app at its best for marketing purposes
 * Represents diverse gay community experiences across major European cities
 */
export const MOCK_CAUCASIAN_EXPLORE_ITEMS: UserRecommendedItem[] = [
  {
    id: "MKT001",
    rank: 1,
    username: "westbourne",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/01.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/01.webp",
      },
      id: "mkt-001",
      likeCount: 184,
      type: UserImageType.PUBLIC_IMAGE,
      username: "westbourne",
    },
    matchingPercentage: 94,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 96,
      },
      {
        rank: 1,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 91,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.TRAVELING,
      Interest.FITNESS,
      Interest.EATING,
      Interest.PHOTOGRAPHY,
    ],
    distance: 1.8,
    profileAttributes: {
      aboutMe:
        "Soho creative who lives for Saturday nights at Heaven and Sunday mornings at Borough Market. Out since uni, proudly building queer community in London one pub quiz at a time. You'll find me at the Royal Vauxhall Tavern or wandering the Tate. Looking for someone who appreciates a proper cuppa and deeper conversations.",
      birthday: "1994-03-22",
      bodyType: BodyType.FIT,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_0,
      weight: Weight.LBS_175,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.NETWORKING,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.TRAVELING,
        Interest.FITNESS,
        Interest.EATING,
        Interest.PHOTOGRAPHY,
        Interest.READING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT002",
    rank: 2,
    username: "nachtleben",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/02.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/02.webp",
      },
      id: "mkt-002",
      likeCount: 213,
      type: UserImageType.PUBLIC_IMAGE,
      username: "nachtleben",
    },
    matchingPercentage: 91,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 93,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SAME_ETHNICITY,
      },
    ],
    commonInterests: [Interest.MUSIC, Interest.FITNESS, Interest.DANCING],
    distance: 3.5,
    profileAttributes: {
      aboutMe:
        "Kreuzberg DJ spinning techno and building queer safe spaces. Berlin is the freedom I always dreamed of growing up in Bavaria. Berghain regular, Schwuz on weekends, Tiergarten jogs to balance it out. Sex-positive, body-positive, radically honest. Looking for someone who can keep up with Berlin nightlife and still make it to brunch.",
      birthday: "1993-08-14",
      bodyType: BodyType.TONED,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_11,
      weight: Weight.LBS_170,
      relationship: null,
      role: RolePosition.TOP,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.EVENTS,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH, Language.GERMAN],
      interests: [
        Interest.MUSIC,
        Interest.FITNESS,
        Interest.DANCING,
        Interest.PHOTOGRAPHY,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT003",
    rank: 3,
    username: "grachten",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/03.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/03.webp",
      },
      id: "mkt-003",
      likeCount: 159,
      type: UserImageType.PUBLIC_IMAGE,
      username: "grachten",
    },
    matchingPercentage: 89,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 91,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.RECENT_ACTIVITY,
      },
    ],
    commonInterests: [Interest.CYCLING, Interest.TRAVELING, Interest.EATING],
    distance: 2.1,
    profileAttributes: {
      aboutMe:
        "Canal-side architect cycling through Amsterdam rain or shine. Reguliersdwarsstraat is my second living room. Volunteer at COC Nederland, weekend trips to Vondelpark, and always down for bitterballen at Cafe 't Mandje. Out and proud in the most tolerant city on earth. Looking for a guy who can ride a bike in dress shoes.",
      birthday: "1995-11-30",
      bodyType: BodyType.SLIM,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_1,
      weight: Weight.LBS_165,
      relationship: null,
      role: RolePosition.BOTTOM,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.COFFEE_DATES,
        Seeking.NETWORKING,
      ],
      languages: [Language.ENGLISH, Language.GERMAN],
      interests: [
        Interest.CYCLING,
        Interest.TRAVELING,
        Interest.EATING,
        Interest.PHOTOGRAPHY,
        Interest.READING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT004",
    rank: 4,
    username: "lumiere",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/04.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/04.webp",
      },
      id: "mkt-004",
      likeCount: 197,
      type: UserImageType.PUBLIC_IMAGE,
      username: "lumiere",
    },
    matchingPercentage: 93,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 94,
      },
      {
        rank: 1,
        type: CompatibilityType.DATING_FORWARD,
        score: 89,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.MANY_SHARED_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.PHOTOGRAPHY,
      Interest.COOKING,
      Interest.TRAVELING,
      Interest.READING,
    ],
    distance: 4.7,
    profileAttributes: {
      aboutMe:
        "Le Marais photographer capturing queer Parisian life. From gallery openings to late nights at Rosa Bonheur, I live for this city's energy. Sunday strolls along the Seine, apero hour at Open Cafe, and yes, I will judge your wine order. Looking for someone cultured who doesn't mind paint-stained fingers and strong opinions.",
      birthday: "1992-06-18",
      bodyType: BodyType.AVERAGE,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_10,
      weight: Weight.LBS_160,
      relationship: null,
      role: RolePosition.VERS_BOTTOM,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.CHAT,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH, Language.FRENCH],
      interests: [
        Interest.PHOTOGRAPHY,
        Interest.COOKING,
        Interest.TRAVELING,
        Interest.READING,
        Interest.CRAFTS,
        Interest.EATING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT005",
    rank: 5,
    username: "solsticio",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/05.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/05.webp",
      },
      id: "mkt-005",
      likeCount: 231,
      type: UserImageType.PUBLIC_IMAGE,
      username: "solsticio",
    },
    matchingPercentage: 90,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 92,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_LANGUAGE,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SIMILAR_AGE,
      },
    ],
    commonInterests: [Interest.DANCING, Interest.FITNESS, Interest.TRAVELING],
    distance: 6.3,
    profileAttributes: {
      aboutMe:
        "Eixample fitness instructor soaking up Barcelona sun year-round. Circuit Festival veteran, Chiringuito beach days, tapas crawls through El Raval. Came out to my family in Seville at 17 and they threw me a party. Proud of my roots, prouder of who I've become. Looking for someone to share sangria and sunsets at Barceloneta.",
      birthday: "1996-01-25",
      bodyType: BodyType.MUSCULAR,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_9,
      weight: Weight.LBS_175,
      relationship: null,
      role: RolePosition.VERS_TOP,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.GYM_BUDDY,
        Seeking.EVENTS,
      ],
      languages: [Language.ENGLISH, Language.SPANISH],
      interests: [
        Interest.DANCING,
        Interest.FITNESS,
        Interest.TRAVELING,
        Interest.EATING,
        Interest.MUSIC,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT006",
    rank: 6,
    username: "nordlys",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/06.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/06.webp",
      },
      id: "mkt-006",
      likeCount: 145,
      type: UserImageType.PUBLIC_IMAGE,
      username: "nordlys",
    },
    matchingPercentage: 87,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 89,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
    ],
    commonInterests: [Interest.HIKING, Interest.READING, Interest.COOKING],
    distance: 9.4,
    profileAttributes: {
      aboutMe:
        "Stockholm designer who traded northern lights for neon lights at Patricia. Fika is a love language and I'm fluent. SideTrack Saturdays, volunteering at Stockholm Pride, and winter swims in Riddarfjarden. Scandinavian calm on the outside, deeply passionate underneath. Looking for someone cozy enough for Swedish winters.",
      birthday: "1993-04-07",
      bodyType: BodyType.TONED,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_2,
      weight: Weight.LBS_185,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.COFFEE_DATES,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.HIKING,
        Interest.READING,
        Interest.COOKING,
        Interest.FITNESS,
        Interest.PHOTOGRAPHY,
        Interest.TRAVELING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT007",
    rank: 7,
    username: "claddagh",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/07.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/07.webp",
      },
      id: "mkt-007",
      likeCount: 168,
      type: UserImageType.PUBLIC_IMAGE,
      username: "claddagh",
    },
    matchingPercentage: 92,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 95,
      },
      {
        rank: 1,
        type: CompatibilityType.DATING_FORWARD,
        score: 88,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.RECENT_ACTIVITY,
      },
    ],
    commonInterests: [
      Interest.WRITING,
      Interest.MUSIC,
      Interest.EATING,
      Interest.TRAVELING,
    ],
    distance: 1.5,
    profileAttributes: {
      aboutMe:
        "Temple Bar writer who came out right when Ireland voted yes on marriage equality. Perfect timing, honestly. Pantibar is church, George's is the after-party. Writing queer Irish stories nobody told us growing up. Pints at The Front Lounge, trad sessions, and long walks through Phoenix Park. Looking for my person, or at least a brilliant conversation.",
      birthday: "1995-05-23",
      bodyType: BodyType.AVERAGE,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_10,
      weight: Weight.LBS_170,
      relationship: null,
      role: RolePosition.BOTTOM,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.CHAT,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.WRITING,
        Interest.MUSIC,
        Interest.EATING,
        Interest.TRAVELING,
        Interest.READING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT008",
    rank: 8,
    username: "maretide",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/08.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/08.webp",
      },
      id: "mkt-008",
      likeCount: 176,
      type: UserImageType.PUBLIC_IMAGE,
      username: "maretide",
    },
    matchingPercentage: 88,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 90,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_LANGUAGE,
      },
    ],
    commonInterests: [Interest.TRAVELING, Interest.EATING, Interest.DANCING],
    distance: 5.2,
    profileAttributes: {
      aboutMe:
        "Bairro Alto bartender living that Lisbon golden hour life. Grew up in Porto, came to Lisbon for the scene, stayed for the community. Trumps on Fridays, beach days at Costa da Caparica, and pasteis de nata as a personality trait. Warm, direct, and always down for a spontaneous road trip to the Algarve. Vamos?",
      birthday: "1997-09-11",
      bodyType: BodyType.FIT,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_5_8,
      weight: Weight.LBS_155,
      relationship: null,
      role: RolePosition.VERS_BOTTOM,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.EVENTS,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH, Language.PORTUGUESE, Language.SPANISH],
      interests: [
        Interest.TRAVELING,
        Interest.EATING,
        Interest.DANCING,
        Interest.MUSIC,
        Interest.PHOTOGRAPHY,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT009",
    rank: 9,
    username: "havnero",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/09.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/03-caucasian-theme/09.webp",
      },
      id: "mkt-009",
      likeCount: 152,
      type: UserImageType.PUBLIC_IMAGE,
      username: "havnero",
    },
    matchingPercentage: 85,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 87,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.MANY_SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SIMILAR_AGE,
      },
    ],
    commonInterests: [
      Interest.CYCLING,
      Interest.COOKING,
      Interest.GARDENING,
      Interest.READING,
    ],
    distance: 7.6,
    profileAttributes: {
      aboutMe:
        "Norrebro urban gardener and sustainability nerd. Copenhagen is the world's most liveable city and I take that personally. Oscar Bar & Cafe for cocktails, Centralhjornet for history, harbour swims at Islands Brygge all summer. Hygge isn't just a word, it's my dating philosophy. Looking for a guy who values slow living and good design.",
      birthday: "1994-12-02",
      bodyType: BodyType.SLIM,
      ethnicity: Ethnicity.WHITE,
      height: Height.FT_6_0,
      weight: Weight.LBS_160,
      relationship: null,
      role: RolePosition.SIDE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.NETWORKING,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.ENGLISH, Language.GERMAN],
      interests: [
        Interest.CYCLING,
        Interest.COOKING,
        Interest.GARDENING,
        Interest.READING,
        Interest.HIKING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
];

export function getCaucasianExploreItems(): UserRecommendedItem[] {
  return [...MOCK_CAUCASIAN_EXPLORE_ITEMS];
}
